import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import '../../../estilos/combobox-de-cuenta.css';
import { PERFIL_DE_CUENTA } from '../logica/perfilesDeSelector.js';

/*
 * T-17-01 · EL CONTROL ÚNICO DE SELECTOR CON BUSCADOR.
 * (RF-230 a RF-234, RF-245, RNF-120, RNF-121, RN-651 a RN-658, RN-677, RN-678,
 *  RN-680; contrato de uso §2 de
 *  `analisis/contratos-edicion-movimientos-y-selectores.md`)
 *
 * ESTE ES EL ÚNICO COMPONENTE DE TODO `src/frontend/src` CON EL COMPORTAMIENTO DEL
 * SELECTOR CON BUSCADOR (RN-658, CA-17-01). Nació como `ComboboxDeCuenta` en el
 * 11/16 y aquí se GENERALIZA: el comportamiento —apertura y cierre, filtrado en
 * memoria, teclado, foco, los tres estados, la elección explícita, el botón de
 * borrar, la región de anuncio y toda la maquetación— se escribe UNA VEZ, y lo que
 * distingue a una cuenta de una categoría son DATOS Y FUNCIONES PURAS que viven en
 * un PERFIL (`logica/perfilesDeSelector.js`, RN-651).
 *
 * DOS COSAS QUE NO PUEDEN APARECER AQUÍ NUNCA:
 *   1. Un `if` por consumidor o por perfil («si es categoría…», «si es el filtro
 *      del historial…»). Eso es la segunda variante del control entrando por la
 *      puerta de atrás, y RN-658 la prohíbe. Lo que un consumidor necesite
 *      distinto se resuelve en el PERFIL (datos) o en el propio consumidor.
 *   2. Una opción que no sea un dato del catálogo. «Todas las categorías» NO es
 *      una opción de la lista (D-369, RN-680): el estado «sin filtro» es el VALOR
 *      VACÍO, al que se llega con el botón de borrar de este control o con el
 *      botón espejo que dibuja el consumidor FUERA del control (RN-677).
 *
 * VIVE EN EL MÓDULO DE MOVIMIENTOS, donde están sus CINCO consumidores (§2.4 del
 * contrato, lista cerrada): los cuatro selectores de cuenta —captura, origen y
 * destino de traspaso, filtro del historial—, el de categoría de las capturas y el
 * filtro CATEGORÍA de «Más filtros…». NO sube al núcleo compartido y no se pide
 * autorización para ello, porque no se toca (D-358, RF-233).
 *
 * LO QUE NO HACE, Y ES CONTRATO (igual con cualquier perfil):
 *   · NO llama al servidor NUNCA (RNF-099/RNF-120). La lista llega ya cargada y el
 *     filtrado es en memoria: cero peticiones por pulsación.
 *   · NO recorta por propietario, tipo, moneda ni por categoría activa: la lista YA
 *     VIENE FILTRADA POR EL SERVIDOR según el propósito de la pantalla (RN-550,
 *     RN-653). Duplicar aquí una regla de servidor es lo que el contrato prohíbe.
 *   · NO guarda preferencias, NO recuerda la última elección y NO sugiere nada: las
 *     precargas que se reflejan vienen del consumidor (RN-657).
 *   · NO elige solo. Escribir no elige, aunque quede una sola coincidencia: la
 *     elección es siempre un acto explícito (RN-553).
 *   · Si la lista llega VACÍA el componente NO SE PINTA y el consumidor muestra su
 *     propio aviso (RN-552, RN-655).
 *
 * TRES ESTADOS, NO DOS (RF-187, RN-562, RN-655, D-296 a D-300):
 *   1. CARGANDO (`cargando` verdadero): rótulo visible + campo DESHABILITADO con el
 *      mismo texto de petición, `aria-busy="true"` en el contenedor, y NI
 *      desplegable, NI lista, NI contador de coincidencias, NI aviso de vacío.
 *   2. LISTA VACÍA (`cargando` falso y cero opciones): no se pinta.
 *   3. CON OPCIONES: todo el comportamiento, sin un solo cambio.
 */

/** Lo que abre el desplegable desde el campo, para el recorrido con teclado. */
const TECLAS_QUE_ABREN = ['ArrowDown', 'ArrowUp'];

/**
 * @param id              identificador del campo, para asociar su etiqueta visible.
 * @param etiqueta        el rótulo visible, que lo pone el consumidor.
 * @param opciones        la lista YA FILTRADA POR EL SERVIDOR. Siempre un arreglo,
 *                        nunca nula (RN-566).
 * @param valor           la identidad elegida, o null/'' si no hay.
 * @param alElegir        recibe la identidad elegida, o null al limpiar.
 * @param perfil          el juego de datos y funciones puras del dato que se elige
 *                        (§2.2). Por omisión, el de cuenta: así los cuatro
 *                        consumidores de cuenta del 11/16 no cambian de conducta.
 * @param textoDePeticion dato opcional del consumidor que pisa el del perfil. Es lo
 *                        que distingue «Elige la cuenta…» de «Todas las cuentas del
 *                        hogar», y ya era entrada del combobox del 11/16.
 * @param obligatorio     si el campo es obligatorio (los filtros no lo son).
 * @param ayuda           texto de apoyo que pone el consumidor.
 * @param cargando        OPCIONAL (RF-187). Verdadero mientras la lista aún no ha
 *                        llegado. Quien no lo pase se comporta como siempre.
 */
export function SelectorConBuscador({
  id,
  etiqueta,
  opciones,
  valor,
  alElegir,
  perfil = PERFIL_DE_CUENTA,
  textoDePeticion,
  obligatorio = false,
  ayuda = null,
  cargando = false,
  datoDePrueba = 'selector-con-buscador',
}) {
  const reactId = useId();
  const idDelCampo = id ?? `selector-con-buscador-${reactId}`;
  const idDeLaLista = `${idDelCampo}-lista`;
  const peticion = textoDePeticion ?? perfil.textoDePeticion;

  const [texto, setTexto] = useState('');
  const [abierta, setAbierta] = useState(false);
  const [activa, setActiva] = useState(-1);

  const campo = useRef(null);
  const contenedor = useRef(null);

  const lista = opciones ?? [];
  const identidadDe = useCallback(
    (opcion) => opcion?.[perfil.claveDeIdentidad],
    [perfil],
  );

  const elegida = useMemo(
    () => lista.find((o) => String(identidadDe(o)) === String(valor ?? '')) ?? null,
    [lista, valor, identidadDe],
  );

  // El filtrado ocurre EN MEMORIA, en cada pulsación, sobre la lista que ya está
  // cargada (RNF-120). Nada aquí habla con el servidor.
  const filtradas = useMemo(() => perfil.filtrar(lista, texto), [perfil, lista, texto]);

  const cerrar = useCallback(() => {
    setAbierta(false);
    setActiva(-1);
  }, []);

  /*
   * RN-553 · El campo arranca SIN VALOR, también cuando hay una sola opción. Lo
   * que sí se refleja es una elección que venga de fuera —una edición, la
   * categoría sugerida de la devolución, el filtro que ya venía en la dirección—:
   * entonces el campo muestra el nombre de esa opción (RN-657).
   */
  useEffect(() => {
    if (!elegida) {
      setTexto('');
      return;
    }
    setTexto(perfil.textoEnElCampo(elegida));
  }, [elegida, perfil]);

  // Cerrar al tocar fuera. Equivale a salir sin elegir: la selección previa se
  // conserva (RN-551).
  useEffect(() => {
    if (!abierta) return undefined;
    const alTocar = (evento) => {
      if (contenedor.current && !contenedor.current.contains(evento.target)) cerrar();
    };
    document.addEventListener('mousedown', alTocar);
    return () => document.removeEventListener('mousedown', alTocar);
  }, [abierta, cerrar]);

  /*
   * RN-552 / RN-655 · Con la lista vacía el campo NO SE PINTA. El consumidor
   * muestra su propio aviso; este componente no lo sustituye.
   *
   * RF-187 · Mientras `cargando` sea verdadero la lista está vacía porque TODAVÍA
   * NO LLEGÓ, no porque no haya nada, y ocultar el campo ahí es lo que hacía que el
   * rótulo apareciera de golpe empujando lo de abajo (RN-564). Con `cargando` falso
   * —acierte o falle la carga, RN-567— manda RN-552 tal cual.
   */
  if (!cargando && lista.length === 0) return null;

  function elegir(indice) {
    const opcion = filtradas[indice];
    if (!opcion) return;
    alElegir?.(identidadDe(opcion));
    setTexto(perfil.textoEnElCampo(opcion));
    cerrar();
    campo.current?.focus();
  }

  /*
   * EL BOTÓN DE BORRAR DEL CONTROL (la ✕): deja el campo vacío Y SIN ELECCIÓN, que
   * es lo que `alElegir` promete en §2.1 («o null al limpiar») y lo que RN-677
   * necesita para que el filtro del historial vuelva al estado «todas» desde el
   * propio control. No es lo mismo que «borrar el texto» de la lista sin
   * coincidencias (RN-551), que está más abajo y NO deselecciona.
   */
  function limpiar() {
    setTexto('');
    setAbierta(true);
    setActiva(-1);
    alElegir?.(null);
    campo.current?.focus();
  }

  /*
   * RN-551 · Dentro de la lista «sin coincidencias» se ofrece borrar el texto, y
   * ESO NO DESELECCIONA: la selección previa no se pierde hasta que se elija otra.
   */
  function borrarElTexto() {
    setTexto('');
    setAbierta(true);
    setActiva(-1);
    campo.current?.focus();
  }

  function escribir(evento) {
    setTexto(evento.target.value);
    setAbierta(true);
    setActiva(-1);
    /*
     * BORRAR EL TEXTO TECLEANDO NO DESELECCIONA (RN-551): la selección previa no
     * se pierde hasta que se elija otra. Para quitar la elección está la ✕, y en
     * los filtros además el botón espejo del consumidor (RN-677).
     */
  }

  function mover(paso) {
    if (filtradas.length === 0) return;
    setAbierta(true);
    setActiva((previa) => {
      const siguiente = previa + paso;
      if (siguiente < 0) return filtradas.length - 1;
      if (siguiente >= filtradas.length) return 0;
      return siguiente;
    });
  }

  /*
   * RNF-121 · Teclado COMPLETO y sin excepciones: flechas para recorrer, Entrar
   * para elegir, Escape para cerrar sin elegir, Tabulador para salir. El foco
   * vuelve al campo al cerrar.
   */
  function teclear(evento) {
    if (TECLAS_QUE_ABREN.includes(evento.key)) {
      evento.preventDefault();
      mover(evento.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (evento.key === 'Enter') {
      // Escribir NO elige. Hay que bajar y pulsar Entrar sobre una opción: la
      // elección es siempre un acto explícito (RN-553).
      if (abierta && activa >= 0) {
        evento.preventDefault();
        elegir(activa);
      }
      return;
    }

    if (evento.key === 'Escape') {
      if (!abierta) return;
      evento.preventDefault();
      cerrar();
      return;
    }

    if (evento.key === 'Tab') cerrar();
  }

  const idDeLaOpcionActiva = activa >= 0 && filtradas[activa]
    ? `${idDeLaLista}-opcion-${activa}`
    : undefined;

  /*
   * RN-562 · Cargando NO HAY DESPLEGABLE. Se calcula aquí, y no se confía en que
   * `abierta` valga falso, porque un reintento (RN-567) vuelve a poner `cargando`
   * en verdadero y el campo pudo quedar abierto antes del fallo.
   */
  const desplegada = abierta && !cargando;

  /*
   * EL MISMO CONTENEDOR Y EL MISMO CAMPO EN LOS DOS ESTADOS (CA-11-38, RN-564): NO
   * se devuelve un árbol aparte para la carga. El `div.cbc`, su etiqueta y el
   * `input` se pintan SIEMPRE en la misma posición, y lo único que cambia son los
   * atributos: así React conserva el nodo del DOM en vez de destruirlo y volver a
   * crearlo, y lo que está debajo no se desplaza al llegar los datos.
   *
   * Lo que sí desaparece cargando es lo que no ocupa sitio: el desplegable (está
   * fuera del flujo) y la región de anuncio (de 1 px, para el lector de pantalla).
   * Quitarlos no mueve un píxel y evita que «ninguna coincide» exista en el DOM
   * mientras no se sabe nada (RN-563).
   */
  return (
    <div
      className="campo cbc"
      ref={contenedor}
      data-prueba={datoDePrueba}
      /* El estado de carga se comunica por el campo DESHABILITADO y por `aria-busy`,
         NUNCA por color y NUNCA por un texto nuevo (RN-563, RNF-121). */
      aria-busy={cargando ? 'true' : undefined}
      data-cargando={cargando ? 'true' : undefined}
      data-perfil={perfil.nombreDelPerfil}
    >
      <label htmlFor={idDelCampo}>{etiqueta}</label>

      <div className="cbc__caja">
        <input
          id={idDelCampo}
          ref={campo}
          className="cbc__campo"
          type="text"
          role="combobox"
          autoComplete="off"
          /* RN-565 · El campo deshabilitado no recibe el foco ni lo roba al
             habilitarse: no hay un solo `focus()` atado a este cambio de estado.
             En las tres capturas el foco inicial sigue siendo el del IMPORTE
             (D-229; en la EDICIÓN, cuando el importe guardado ya está presentado,
             RN-660). */
          disabled={cargando}
          aria-expanded={desplegada ? 'true' : 'false'}
          aria-controls={idDeLaLista}
          aria-autocomplete="list"
          aria-activedescendant={idDeLaOpcionActiva}
          aria-required={obligatorio ? 'true' : undefined}
          placeholder={peticion}
          value={texto}
          onChange={escribir}
          onFocus={() => setAbierta(true)}
          onKeyDown={teclear}
          data-prueba={`${datoDePrueba}-campo`}
        />
        {!cargando && texto !== '' && (
          <button
            type="button"
            className="cbc__borrar"
            onClick={limpiar}
            aria-label="Borrar el texto"
            data-prueba={`${datoDePrueba}-borrar`}
          >
            ✕
          </button>
        )}
      </div>

      {!cargando && (
      <ul
        id={idDeLaLista}
        role="listbox"
        /* El nombre accesible de la lista NO repite el del campo, y no es cosmética:
           con los dos iguales, el rótulo de la etiqueta identificaba DOS elementos a
           la vez y cualquier búsqueda por etiqueta —la de una persona con lector de
           pantalla, igual que la de una prueba— encontraba dos cosas donde hay un
           campo. El campo ya toma su nombre de la etiqueta; la lista dice qué es. */
        aria-label={perfil.nombreAccesibleDeLaLista}
        className={`cbc__lista${desplegada ? ' cbc__lista--abierta' : ''}`}
        data-prueba={`${datoDePrueba}-lista`}
      >
        {filtradas.length === 0 ? (
          /* RN-551 · Sin coincidencias NO SE OCULTA LA LISTA ni se cierra el campo,
             y la selección previa no se pierde hasta que se elija otra. */
          <li className="cbc__nada" role="option" aria-disabled="true" aria-selected="false">
            {perfil.textoSinCoincidencias(texto)}
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={borrarElTexto}>
              Borrar el texto
            </button>
          </li>
        ) : (
          filtradas.map((opcion, indice) => {
            /* TODA OPCIÓN DE LA LISTA ES UN DATO DEL CATÁLOGO (RN-680): aquí no se
               inventa ningún renglón, ni con el buscador vacío ni con texto. */
            const datos = perfil.describirDatos(opcion);
            return (
              <li
                key={identidadDe(opcion)}
                id={`${idDeLaLista}-opcion-${indice}`}
                role="option"
                aria-selected={indice === activa ? 'true' : 'false'}
                aria-label={perfil.describirParaLector(opcion)}
                className="cbc__opcion"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => elegir(indice)}
                data-prueba={`${datoDePrueba}-opcion`}
              >
                <span className="cbc__nombre">{perfil.describirNombre(opcion)}</span>
                {datos !== '' && <span className="cbc__datos">{datos}</span>}
              </li>
            );
          })
        )}
      </ul>
      )}

      {/* El número de coincidencias se anuncia por una región aria-live discreta
          (RNF-121): se oye, no se ve. Cargando NO se anuncia nada: no hay
          coincidencias que contar y decir «ninguna» sería falso (RN-563). */}
      {!cargando && (
        <span className="cbc__anuncio" role="status" aria-live="polite">
          {desplegada ? perfil.anunciarCoincidencias(filtradas.length) : ''}
        </span>
      )}

      {ayuda && <span className="ayuda">{ayuda}</span>}
    </div>
  );
}

export default SelectorConBuscador;
